import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useManualQuery } from 'graphql-hooks'
import type { IAddon } from '@vacationtracker/shared/types/addons'

import { getAutomations, getPurchasedAddons } from '../../graphql/custom-queries'
import { track } from '../../services/analytics/analytics'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { useEffectivePlan } from '../../store/use-effective-plan'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { useFeatureInterest } from '../../hooks/use-feature-interest'
import { getExtendedAddons, purchasedStatuses } from '../Add-ons/common/addons'
import { useAddonActions } from '../../components/addon-actions'
import { CatalogCard, CatalogCardGrid } from '../../components/catalog-card'
import type { CatalogCardProps } from '../../components/catalog-card'
import { NotifyMe } from '../../components/catalog-card/notify-me'
import { CatalogFilter } from '../../components/catalog-filter'
import { MovedNote } from '../../components/moved-note'
import AddonFeedback from '../../components/addon-feedback'
import RequestAddon from '../../components/request-addon'
import { Button } from '../../design-system/Button'
import { EmptyState } from '../../design-system/EmptyState'
import { LoadingIndicator } from '../../design-system/LoadingIndicator'
import { PageHeader } from '../../design-system/PageHeader'
import type { IGetAutomations, IGetPurchasedAddonsData } from '../../types/custom-queries'
import { FrontendUrls } from '../../types/urls'

import { getAddonTiles, isAddonActive } from './catalog'
import type { AddonTile } from './catalog'

const MOVED_NOTE_KEY = 'vt-addons-moved-note-dismissed'

type AddonEntry = ReturnType<typeof getExtendedAddons>[number]

interface PageData {
  subscribed: IAddon[]
  purchased: Record<string, string>
  /** Null when the rules could not be read: cards then show no status rather than a wrong one. */
  withRuleOn: Set<string> | null
}

interface AddonGridProps extends PageData {
  onActivated: () => void
}

function AddonsMovedNote({ userId }: { userId: string }): React.ReactElement {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const names = {
    api: formatMessage({ id: 'integrationsPage.name.api' }),
    zapier: formatMessage({ id: 'integrationsPage.name.zapier' }),
    adp: formatMessage({ id: 'integrationsPage.name.adp' }),
  }
  return (
    <MovedNote
      storageKey={`${MOVED_NOTE_KEY}-${userId}`}
      message={formatMessage({ id: 'addonsPage.movedNote' }, names)}
      action={{ label: formatMessage({ id: 'addonsPage.movedNoteLink' }), onClick: () => history.push(FrontendUrls.integrations) }}
    />
  )
}

function AddonGrid({ subscribed, purchased, withRuleOn, onActivated }: AddonGridProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const effectivePlan = useEffectivePlan()
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const interestFor = useFeatureInterest(authUser.id)
  const isAdmin = authUser.role === 'Admin'

  const entries = useMemo(
    () =>
      getExtendedAddons(formatMessage, featureFlags).sort((a, b) => formatMessage({ id: `automations.${a.type}` }).localeCompare(formatMessage({ id: `automations.${b.type}` }))),
    [featureFlags, formatMessage]
  )
  const entryByType = useMemo(() => new Map<string, AddonEntry>(entries.map((entry) => [entry.type, entry])), [entries])

  const { activate, modals } = useAddonActions({ allSubscribedAddons: subscribed, amIAdmin: isAdmin, onActivated })

  const tiles = getAddonTiles({
    entries,
    plan: effectivePlan ?? '',
    // A location admin who approves gets the approver add-ons, like the add-on create routes allow
    role: authUser.role === 'LocationAdmin' && authUser.approverTo?.length > 0 ? 'Approver' : authUser.role,
    purchased,
    withRuleOn: withRuleOn ?? new Set(),
  })

  const hasStatus = (tile: AddonTile) => Boolean(withRuleOn) && tile.status !== 'comingSoon'

  const actionProps = (tile: AddonTile): Pick<CatalogCardProps, 'href' | 'external' | 'onSelect'> => {
    const { action } = tile
    if (action.kind === 'view' || action.kind === 'create') {
      return { href: action.href, onSelect: () => void track('ADDONS_MODULE_OPENED', { addonName: tile.type }) }
    }
    if (action.kind === 'external') {
      return { href: action.href, external: true }
    }
    if (action.kind === 'activate') {
      return { onSelect: () => activate(tile.type) }
    }
    return {}
  }

  const renderCards = (shown: AddonTile[]) => (
    <CatalogCardGrid>
      {shown.map((tile) => {
        const entry = entryByType.get(tile.type) as AddonEntry
        const name = formatMessage({ id: `automations.${tile.type}` })
        const comingSoon = tile.status === 'comingSoon'
        return (
          <CatalogCard
            key={tile.type}
            title={name}
            logo={entry.icon}
            description={entry.description}
            status={hasStatus(tile) && tile.status !== 'comingSoon' ? tile.status : undefined}
            comingSoon={comingSoon}
            adminsOnly={tile.adminsOnly}
            lock={tile.lock}
            onSeePlans={isAdmin ? () => void track('SEE_PLANS_CLICKED', { addon: tile.type }) : undefined}
            notify={comingSoon && !tile.adminsOnly ? <NotifyMe interest={interestFor(tile.type, name)} /> : undefined}
            {...actionProps(tile)}
          />
        )
      })}
    </CatalogCardGrid>
  )

  return (
    <>
      <CatalogFilter
        items={tiles}
        isActive={isAddonActive}
        hasStatus={hasStatus}
        emptyTitles={{ active: formatMessage({ id: 'addonsPage.activeEmpty' }), inactive: formatMessage({ id: 'addonsPage.inactiveEmpty' }) }}
        render={renderCards}
      />
      {modals}
    </>
  )
}

/** The leave rules a company can turn on: what each does and whether it is running. */
const AddonsPage = (): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const isAdmin = authUser.role === 'Admin'
  const isMounted = useRef(false)

  const [data, setData] = useState<PageData | null>(null)
  const [failed, setFailed] = useState(false)
  const [showRequestAddon, setShowRequestAddon] = useState(false)

  const [getPurchasedAddonsQuery] = useManualQuery<IGetPurchasedAddonsData>(getPurchasedAddons)
  const [getAutomationsQuery] = useManualQuery<IGetAutomations>(getAutomations)

  const load = async () => {
    setFailed(false)
    const [purchasedResponse, automationsResponse] = await Promise.all([getPurchasedAddonsQuery(), getAutomationsQuery()])
    if (!isMounted.current) return
    if (!purchasedResponse.data || purchasedResponse.error) {
      setFailed(true)
      return
    }
    const result = purchasedResponse.data.getPurchasedAddons
    const rules = automationsResponse.data && !automationsResponse.error ? automationsResponse.data.getAutomations : null
    setData({
      subscribed: [...(result?.addons || []), ...(result?.automations || []), ...(result?.integrations || [])] as IAddon[],
      purchased: purchasedStatuses(result),
      withRuleOn: rules ? new Set(rules.filter((row) => row.isActive).map((row) => row.automationType as string)) : null,
    })
  }

  useEffect(() => {
    isMounted.current = true
    track('AUTOMATIONS_ACTIVE_VIEWED', {})
    load()
    return () => {
      isMounted.current = false
    }
  }, [])

  const headerActions = (
    <>
      <AddonFeedback page="automation-page-list" />
      <Button
        variant="outline"
        onClick={() => {
          setShowRequestAddon(true)
          track('AUTOMATION_REQUEST_FORM_VIEWED', {})
        }}
      >
        {formatMessage({ id: 'automations.requestAnAutomation' })}
      </Button>
    </>
  )

  const body = () => {
    if (failed) {
      return (
        <EmptyState variant="full" title={formatMessage({ id: 'addonsPage.loadError' })} action={{ label: formatMessage({ id: 'app.tryAgain' }), onClick: () => void load() }} />
      )
    }
    if (!data) {
      return <LoadingIndicator size="large" />
    }
    return <AddonGrid {...data} onActivated={() => void load()} />
  }

  return (
    <div className="main-content">
      <PageHeader title={formatMessage({ id: 'app.addons' })} actions={headerActions} />
      <div className="main-content-body">
        {isAdmin && <AddonsMovedNote userId={authUser.id} />}
        {body()}
      </div>
      <RequestAddon visible={showRequestAddon} onCancel={() => setShowRequestAddon(false)} />
    </div>
  )
}

export default AddonsPage
